import { Injectable } from '@angular/core';

// Utils
import { Logger } from '../logger/logger.service';

const LOGGER = new Logger('ClientContextService');

/** Ancienne clé de contexte, source de vérité jusqu'à #173. Supprimée au profit de l'URL. */
const LEGACY_CLIENT_KEY = 'Client';
/** Préférence d'ouverture : dernier dossier travaillé. Lue uniquement à la racine `/`. */
const LAST_CLIENT_KEY = 'lastClientId';

/**
 * Porte l'identifiant du dossier courant pour la couche HTTP.
 *
 * Ce service est **sans dépendance** à dessein : il est lu par l'intercepteur
 * `apiClientInterceptor` à chaque requête, et l'injection d'`AppService` à cet
 * endroit ferait construire le graphe de services au milieu d'une requête.
 *
 * La source de vérité du dossier reste l'**URL** (cf. `ClientContextGuard`) ;
 * `AppService.selectedClient` en est la façade applicative et alimente ce
 * service. `lastClientId` n'est qu'une préférence d'ouverture, jamais une
 * source de vérité : aucun écran ne la lit.
 */
@Injectable({
    providedIn: 'root'
})
export class ClientContextService {

    /*----------------------------------------------------------------------------------------
     * Données du service
     ----------------------------------------------------------------------------------------*/

    private _clientId: number | null = null;

    /*----------------------------------------------------------------------------------------
     * Constructeur
     ----------------------------------------------------------------------------------------*/

    constructor() {
        this._migrateLegacyKey();
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get clientId(): number | null {
        return this._clientId;
    }
    public set clientId(clientId: number | null) {
        this._clientId = clientId;
        if (clientId !== null) {
            localStorage.setItem(LAST_CLIENT_KEY, clientId.toString());
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Oublie le dossier courant **et** la préférence d'ouverture.
     *
     * Appelé au passage par l'écran de connexion, qui est le point unique par lequel
     * transitent la déconnexion explicite comme l'expiration de session. Passer par
     * `/login` dit « je quitte » : le prochain arrivant sur ce navigateur ne doit pas
     * hériter du dossier du précédent.
     *
     * Sans conséquence sur la reprise du travail : `AuthenticationGuard` conserve
     * l'URL complète — dossier compris — dans son paramètre `redirect`.
     */
    public forget(): void {
        this._clientId = null;
        localStorage.removeItem(LAST_CLIENT_KEY);
    }

    /**
     * Dernier dossier travaillé. **Réservé à la redirection depuis `/`** : aucun
     * autre appelant ne doit s'en servir, sous peine de recréer l'état caché que
     * #173 supprime.
     */
    public readLastClientId(): number | null {
        const clientId = Number(localStorage.getItem(LAST_CLIENT_KEY));
        return Number.isInteger(clientId) && clientId > 0 ? clientId : null;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Reprend l'ancienne clé `Client` en préférence d'ouverture, puis la supprime :
     * l'utilisateur retrouve son dernier dossier après la mise en production, sans
     * qu'il subsiste une seconde source de contexte.
     */
    private _migrateLegacyKey(): void {
        const legacyClientId = localStorage.getItem(LEGACY_CLIENT_KEY);
        if (!legacyClientId) {
            return;
        }
        if (!localStorage.getItem(LAST_CLIENT_KEY)) {
            LOGGER.debug('Reprise de l\'ancienne clé de contexte en préférence d\'ouverture');
            localStorage.setItem(LAST_CLIENT_KEY, legacyClientId);
        }
        localStorage.removeItem(LEGACY_CLIENT_KEY);
    }
}
